// My Desk layout — GET/PUT /api/v1/desk/layout (src/desk.js), Request Ledger
// N-090: "every manager gets their own canvas". Follows favorites.ts's own
// shape (fetch + useResource + persistResource) for the read side; the
// write side debounces, since arrange mode saves on every drag/resize
// rather than on an explicit "Save" click.
import { authedGet, authedWrite } from './apiClient';
import { useResource, refetchResource, persistResource, peekResource } from './store';
import type { DeskLayout } from '../desk/panelCatalog';

export const DESK_LAYOUT_KEY = 'desk-layout:v1';

export interface DeskLayoutResponse {
  layout: DeskLayout | null;
  updatedAt?: string | null;
  /** The login this layout belongs to — same purpose as favorites.ts's
   * `owner`: tells "this cache entry is THIS login's" from "this cache
   * entry is whoever was last at this desktop" (cachedDeskLayoutIsFor). */
  owner?: string;
  /** Owner, 2026-10-12 ("LOCK DESK"): true when this manager's or
   * director's Desk is locked to Tomer's own — the layout above IS his (as
   * their role sees it) and it cannot be changed or saved. */
  locked?: boolean;
  /** Owner, 2026-10-12 ("I want to edit the layout for myself and that
   * carries to everyone"): the layout is Tomer's own Desk, not this
   * person's (locked, or unlocked with nothing saved of their own). With
   * `layout: null` it means Tomer never saved one — his shipped default. */
  fromOwner?: boolean;
  /** A manager's or director's response only: Tomer's Desk as their role
   * sees it (null = his shipped default) — what Reset layout goes back to. */
  ownerLayout?: DeskLayout | null;
  /** The owner's own response only: whether every manager and director is
   * locked to his Desk — the Lock desks / Unlock desks button. */
  desksLocked?: boolean;
}

export async function fetchDeskLayout(): Promise<DeskLayoutResponse> {
  const data = await authedGet<DeskLayoutResponse>('/api/v1/desk/layout');
  await persistResource(DESK_LAYOUT_KEY, data);
  return data;
}

/** True only when the cached layout is this login's — mirrors
 * favorites.ts's cachedFavoritesAreFor exactly, same rationale. */
export function cachedDeskLayoutIsFor(email: string | null | undefined): boolean {
  if (!email) return false;
  const data = peekResource<DeskLayoutResponse>(DESK_LAYOUT_KEY);
  return Boolean(data && data.owner === email);
}

export function useDeskLayout(): ReturnType<typeof useResource<DeskLayoutResponse>> {
  return useResource<DeskLayoutResponse>(DESK_LAYOUT_KEY, fetchDeskLayout);
}

export function reloadDeskLayout(): Promise<void> {
  return refetchResource(DESK_LAYOUT_KEY, fetchDeskLayout);
}

export function peekDeskLayout(): DeskLayout | null {
  return peekResource<DeskLayoutResponse>(DESK_LAYOUT_KEY)?.layout ?? null;
}

/** One PUT, right now — no debounce. Exported separately so a caller that
 * wants to force an immediate save (e.g. "Reset to default", or a test)
 * does not have to wait out the debounce window. */
export async function saveDeskLayoutNow(layout: DeskLayout): Promise<DeskLayoutResponse> {
  const result = await authedWrite<DeskLayoutResponse>('PUT', '/api/v1/desk/layout', layout);
  // Kept from the cached entry: who it belongs to, and the owner's lock
  // switch. Once saved the Desk is this person's own, not Tomer's.
  const current = peekResource<DeskLayoutResponse>(DESK_LAYOUT_KEY);
  const next: DeskLayoutResponse = {
    ...(current ?? {}), layout: result.layout ?? layout, updatedAt: result.updatedAt ?? null, fromOwner: false,
  };
  await persistResource(DESK_LAYOUT_KEY, next);
  return next;
}

/** Lock desks / Unlock desks (owner only, owner 2026-10-12) — one PUT
 * /api/v1/desk/lock for every manager and director. The cached Desk entry
 * carries the new switch at once. */
export async function saveDesksLocked(locked: boolean): Promise<boolean> {
  const result = await authedWrite<{ locked?: boolean }>('PUT', '/api/v1/desk/lock', { locked });
  const now = result.locked ?? locked;
  const current = peekResource<DeskLayoutResponse>(DESK_LAYOUT_KEY);
  if (current) await persistResource(DESK_LAYOUT_KEY, { ...current, desksLocked: now });
  return now;
}

export const DESK_LAYOUT_SAVE_DEBOUNCE_MS = 600;

let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let pendingLayout: DeskLayout | null = null;

/** Save on change, debounced — the canvas calls this after every resize,
 * move, add, or remove, and a burst of them (a drag firing many times) still
 * produces exactly one PUT once the person pauses. `onSaved`, when given,
 * fires once that one PUT actually succeeds — HomePage uses it to flash a
 * "Saved" caption tied to the real write completing, not just to the person
 * having made an edit (N-090b build spec item 5). A later call's `onSaved`
 * simply replaces an earlier one still waiting out the debounce window —
 * the same edits are being saved either way, so only the most recent
 * caller's callback needs to run. */
export function scheduleDeskLayoutSave(layout: DeskLayout, onSaved?: () => void): void {
  pendingLayout = layout;
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    const toSave = pendingLayout;
    pendingLayout = null;
    if (toSave) void saveDeskLayoutNow(toSave).then(() => {
      onSaved?.();
    }).catch((err) => {
      console.error('desk layout save failed:', err);
    });
  }, DESK_LAYOUT_SAVE_DEBOUNCE_MS);
}

/** Test-only: run a pending debounced save immediately instead of waiting
 * out the real timer. Returns null when nothing is pending. */
export function flushPendingDeskLayoutSave(): Promise<DeskLayoutResponse> | null {
  if (!pendingTimer) return null;
  clearTimeout(pendingTimer);
  pendingTimer = null;
  const toSave = pendingLayout;
  pendingLayout = null;
  return toSave ? saveDeskLayoutNow(toSave) : null;
}

/** Test-only: drop a pending debounced save without running it. */
export function cancelPendingDeskLayoutSave(): void {
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = null;
  pendingLayout = null;
}
